// OverdueRentalsPage - Kintlévőség lista (lejárt és vissza nem hozott gépek)
// Epic 57, Story 57-9
import { RentalStatus } from '@/api/rentals';
import { Button, Card, Input } from '@/components/ui';
import { useRentalExpirations, useRentals } from '@/hooks/use-rentals';
import { cn, formatDateSmart } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SmsReminderModal } from './components/SmsReminderModal';

/**
 * Returns the number of days a rental is overdue (today - expectedReturnDate).
 * Returns 0 if not yet overdue.
 */
function calculateDaysOverdue(expectedReturnDate: string): number {
  const expected = new Date(expectedReturnDate);
  const today = new Date();
  // Zero out time portion for accurate day comparison
  expected.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffMs = today.getTime() - expected.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}

/**
 * Returns Tailwind classes for color-coding based on overdue days.
 * >7 days: red, 3-7 days: orange, 1-2 days: yellow
 */
function getOverdueColorClasses(days: number): {
  row: string;
  badge: string;
  text: string;
} {
  if (days > 7) {
    return {
      row: 'border-l-4 border-l-red-500 dark:border-l-red-400',
      badge: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200',
      text: 'text-red-600 dark:text-red-400',
    };
  }
  if (days >= 3) {
    return {
      row: 'border-l-4 border-l-orange-500 dark:border-l-orange-400',
      badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200',
      text: 'text-orange-600 dark:text-orange-400',
    };
  }
  return {
    row: 'border-l-4 border-l-yellow-500 dark:border-l-yellow-400',
    badge: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200',
    text: 'text-yellow-600 dark:text-yellow-400',
  };
}

const formatCurrency = (amount: number) => `${amount.toLocaleString('hu-HU')} Ft`;

/**
 * Returns Tailwind classes for color-coding debt vs deposit coverage.
 * ratio >= 1: debt exceeds deposit → red + bold
 * ratio >= 0.8: approaching deposit limit → orange
 * ratio < 0.8: within deposit coverage → green
 * no deposit: muted
 */
function getDebtColorClasses(currentDebt: number, depositPaid: number): string {
  if (depositPaid <= 0) return 'text-muted-foreground';
  const ratio = currentDebt / depositPaid;
  if (ratio >= 1) return 'text-red-600 dark:text-red-400 font-bold';
  if (ratio >= 0.8) return 'text-orange-600 dark:text-orange-400';
  return 'text-green-600 dark:text-green-400';
}

/**
 * „Kit hívjak" — partner-telefon click-to-call (`tel:`) link a lejárt-sorokon (39-kör FE-2).
 * A `stopPropagation` megakadályozza, hogy a sor-kattintás (rental-detail navigáció) elsüljön.
 */
function PartnerPhoneLink({
  phone,
  testId,
  label,
}: {
  phone: string;
  testId: string;
  label: string;
}) {
  return (
    <a
      href={`tel:${phone}`}
      data-testid={testId}
      aria-label={`${label}: ${phone}`}
      onClick={e => e.stopPropagation()}
      className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
      </svg>
      {phone}
    </a>
  );
}

export function OverdueRentalsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Fetch overdue + active rentals (active with past expectedReturnDate are also overdue)
  const {
    rentals: overdueRentals,
    loading: loadingOverdue,
    error: errorOverdue,
  } = useRentals({
    status: RentalStatus.OVERDUE,
    search: search.trim() || undefined,
  });
  const {
    rentals: activeRentals,
    loading: loadingActive,
    error: errorActive,
  } = useRentals({
    status: RentalStatus.ACTIVE,
    search: search.trim() || undefined,
  });
  const loading = loadingOverdue || loadingActive;
  // 41-kör FE-3 quick-win: eddig hardcoded null volt (dead error-state, Story 105-1
  // skip-jelölés) — honest-state: bármelyik lista-hívás hibája látható hibablokkot ad.
  const error = errorOverdue ?? errorActive;

  // 39-kör FE-2: „kit hívjak" — partner-telefon a lejárt-sorokra (GET /rental-expirations)
  const { byRentalId: expirationsByRental } = useRentalExpirations();

  // Merge: OVERDUE + ACTIVE with past expectedReturnDate
  const now = new Date();
  const rentals = useMemo(() => {
    const pastActiveRentals = activeRentals.filter(r => new Date(r.expectedReturnDate) < now);
    const ids = new Set(overdueRentals.map(r => r.id));
    return [...overdueRentals, ...pastActiveRentals.filter(r => !ids.has(r.id))];
  }, [overdueRentals, activeRentals]);

  // Sort by most overdue first (oldest expectedReturnDate at top)
  const sortedRentals = useMemo(() => {
    return [...rentals].sort(
      (a, b) => new Date(a.expectedReturnDate).getTime() - new Date(b.expectedReturnDate).getTime()
    );
  }, [rentals]);

  const overdueCount = sortedRentals.length;

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/rental')}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('finance.receivables')}
                </h1>
                {!loading && (
                  <span className="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/50 px-2.5 py-0.5 text-sm font-medium text-red-800 dark:text-red-200">
                    {overdueCount}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('rental.overdueSubtitle')}
              </p>
            </div>
          </div>

          <Button variant="outline" onClick={() => navigate('/rental')}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
            {t('rental.allRentals')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Search filter */}
        <Card className="mb-6 p-4">
          <Input
            placeholder={t('rental.searchPartnerPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full"
          />
        </Card>

        {/* Loading state */}
        {loading && (
          <div className="py-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <Card className="p-6 text-center border-red-200 dark:border-red-800">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              {t('common.retry')}
            </Button>
          </Card>
        )}

        {/* Overdue rentals table */}
        {!loading && !error && sortedRentals.length > 0 && (
          <Card className="overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.partnerNameCol')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.equipmentNameCol')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.expiryDate')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.daysOverdue')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.rentalCodeCol')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.amountCol')}
                    </th>
                    {/* 41-kör FE-2 (#755 pin): várható díj — SSoT-előnézet oszlop */}
                    <th
                      className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                      title={t('rental.estimatedLateFeeHint')}
                    >
                      {t('rental.estimatedLateFeeCol')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('rental.currentDebt')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {sortedRentals.map(rental => {
                    const daysOverdue = calculateDaysOverdue(rental.expectedReturnDate);
                    const colors = getOverdueColorClasses(daysOverdue);
                    const firstItem = rental.items[0];
                    const serialNumber = firstItem?.serialNumber ?? '';

                    const currentDebt = rental.currentDebt ?? 0;
                    const partnerPhone = expirationsByRental.get(rental.id)?.partnerPhone;

                    return (
                      <tr
                        key={rental.id}
                        className={cn(
                          'cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-slate-800/50',
                          colors.row
                        )}
                        onClick={() => navigate(`/rental/${rental.id}`)}
                      >
                        {/* Partner neve + „kit hívjak" telefon */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {rental.customerName}
                          </div>
                          {partnerPhone && (
                            <div className="flex flex-col items-start gap-0.5">
                              <PartnerPhoneLink
                                phone={partnerPhone}
                                testId={`partner-phone-${rental.id}`}
                                label={t('rental.callPartner')}
                              />
                              <SmsReminderModal
                                phone={partnerPhone}
                                customerName={rental.customerName}
                                equipmentName={rental.equipmentName}
                                daysOverdue={daysOverdue}
                                testId={`sms-reminder-btn-${rental.id}`}
                              />
                            </div>
                          )}
                        </td>

                        {/* Gép megnevezése + sorozatszám */}
                        <td className="px-4 py-3">
                          <div className="text-gray-900 dark:text-gray-100">
                            {rental.equipmentName}
                          </div>
                          {serialNumber && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              S/N: {serialNumber}
                            </div>
                          )}
                        </td>

                        {/* Lejárat dátuma */}
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {formatDateSmart(rental.expectedReturnDate)}
                        </td>

                        {/* Késés napja */}
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold',
                              colors.badge
                            )}
                          >
                            {daysOverdue} {t('rental.daysLabel')}
                          </span>
                        </td>

                        {/* Bérlési kód */}
                        <td className="px-4 py-3">
                          <button
                            className="font-mono text-sm font-medium text-primary hover:underline"
                            onClick={e => {
                              e.stopPropagation();
                              navigate(`/rental/${rental.id}`);
                            }}
                          >
                            {rental.rentalCode}
                          </button>
                        </td>

                        {/* Összeg */}
                        <td className="px-4 py-3 text-right">
                          <div className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                            {formatCurrency(rental.totalAmount)}
                          </div>
                          {rental.lateFeeAmount > 0 && (
                            <div className="text-xs tabular-nums text-red-600 dark:text-red-400">
                              + {formatCurrency(rental.lateFeeAmount)} {t('rental.lateFeeLabel')}
                            </div>
                          )}
                        </td>

                        {/* 41-kör FE-2 (#755 pin): várható késedelmi díj — a wire-értéket
                            rendereli 1:1 (0 FE-számítás); mező-hiány = nincs díj (»–«) */}
                        <td
                          className="px-4 py-3 text-right"
                          title={t('rental.estimatedLateFeeHint')}
                        >
                          {typeof rental.estimatedLateFee === 'number' ? (
                            <div
                              className="font-medium tabular-nums text-red-600 dark:text-red-400"
                              data-testid={`estimated-late-fee-${rental.id}`}
                            >
                              {formatCurrency(rental.estimatedLateFee)}
                            </div>
                          ) : (
                            <span
                              className="text-xs text-muted-foreground"
                              data-testid={`estimated-late-fee-empty-${rental.id}`}
                            >
                              &ndash;
                            </span>
                          )}
                        </td>

                        {/* Kintlevőség */}
                        <td className="px-4 py-3 text-right">
                          <div
                            className={cn(
                              'font-medium tabular-nums',
                              getDebtColorClasses(currentDebt, rental.depositPaid)
                            )}
                          >
                            {formatCurrency(currentDebt)}
                          </div>
                          <div className="text-xs tabular-nums text-muted-foreground">
                            {t('rental.deposit')}: {formatCurrency(rental.depositPaid)}
                          </div>
                          {currentDebt > 0 &&
                            rental.depositPaid > 0 &&
                            currentDebt >= rental.depositPaid && (
                              <div className="text-xs text-red-600 dark:text-red-400 font-semibold">
                                {t('rental.debtExceedsDeposit')}
                              </div>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-slate-700">
              {sortedRentals.map(rental => {
                const daysOverdue = calculateDaysOverdue(rental.expectedReturnDate);
                const colors = getOverdueColorClasses(daysOverdue);
                const firstItem = rental.items[0];
                const serialNumber = firstItem?.serialNumber ?? '';
                const mobileDebt = rental.currentDebt ?? 0;
                const mobilePartnerPhone = expirationsByRental.get(rental.id)?.partnerPhone;

                return (
                  <div
                    key={rental.id}
                    className={cn(
                      'p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-slate-800/50',
                      colors.row
                    )}
                    onClick={() => navigate(`/rental/${rental.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {rental.customerName}
                      </span>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold',
                          colors.badge
                        )}
                      >
                        {daysOverdue} {t('rental.daysLateLabel')}
                      </span>
                    </div>
                    {mobilePartnerPhone && (
                      <div className="mb-1 flex flex-col items-start gap-0.5">
                        <PartnerPhoneLink
                          phone={mobilePartnerPhone}
                          testId={`partner-phone-mobile-${rental.id}`}
                          label={t('rental.callPartner')}
                        />
                        <SmsReminderModal
                          phone={mobilePartnerPhone}
                          customerName={rental.customerName}
                          equipmentName={rental.equipmentName}
                          daysOverdue={daysOverdue}
                          testId={`sms-reminder-btn-mobile-${rental.id}`}
                        />
                      </div>
                    )}
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {rental.equipmentName}
                      {serialNumber ? ` (S/N: ${serialNumber})` : ''}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('rental.expired')}: {formatDateSmart(rental.expectedReturnDate)}
                      </span>
                      <span className="font-mono text-xs text-primary">{rental.rentalCode}</span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(rental.totalAmount)}
                      {rental.lateFeeAmount > 0 && (
                        <span className="ml-2 text-xs text-red-600 dark:text-red-400">
                          + {formatCurrency(rental.lateFeeAmount)} {t('rental.lateFeeLabel')}
                        </span>
                      )}
                    </div>
                    {/* 41-kör FE-2 (#755 pin): várható díj sor — mobil (csak ha a wire adja) */}
                    {typeof rental.estimatedLateFee === 'number' && (
                      <div
                        className="mt-1 flex items-center justify-between text-sm"
                        data-testid={`estimated-late-fee-mobile-${rental.id}`}
                      >
                        <span className="text-muted-foreground">
                          {t('rental.estimatedLateFeeCol')}:
                        </span>
                        <span className="font-medium text-red-600 dark:text-red-400">
                          {formatCurrency(rental.estimatedLateFee)}
                        </span>
                      </div>
                    )}
                    {/* Kintlevőség sor — mobil */}
                    <div className="mt-1 flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('rental.currentDebt')}:</span>
                      <span
                        className={cn(
                          'font-medium',
                          getDebtColorClasses(mobileDebt, rental.depositPaid)
                        )}
                      >
                        {formatCurrency(mobileDebt)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('rental.deposit')}: {formatCurrency(rental.depositPaid)}
                    </div>
                    {mobileDebt > 0 &&
                      rental.depositPaid > 0 &&
                      mobileDebt >= rental.depositPaid && (
                        <div className="mt-0.5 text-xs text-red-600 dark:text-red-400 font-semibold">
                          {t('rental.debtExceedsDeposit')}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Empty state */}
        {!loading && !error && sortedRentals.length === 0 && (
          <div className="py-16 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mx-auto h-16 w-16 text-green-400 dark:text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('rental.noOverdue')}
            </h3>
            <p className="mt-1 text-gray-500 dark:text-gray-400">{t('rental.allReturned')}</p>
            <Button variant="outline" className="mt-6" onClick={() => navigate('/rental')}>
              {t('rental.backToRentals')}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
